import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarProvider,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import type { NavigationUser } from "../NavigationUserMenu/types";
import { getStoredSessionRedirectPath } from "../../utils/authentication";
import { SidebarNavItem, SidebarToggle, SidebarUserMenu, sidebarStyle } from "../SidebarNavigation";
import { getActiveTrail, getVisibleNavigation } from "./config";
import { CollapsedNav } from "./components/CollapsedNav";
import { CountrySwitcher } from "./components/CountrySwitcher";
import { ExpandedNav } from "./components/ExpandedNav";
import type { Country, ManagerNavigation, WfpManagerMainNavigationProps } from "./types";

type ManagerSidebarProps = {
  activeSectionId?: string;
  country: Country;
  navigation: ManagerNavigation;
  onCountryChange: (countryCode: string) => void;
  user?: NavigationUser;
};

const Logo = () => {
  const { t } = useTranslation();

  return (
    <Link
      to={getStoredSessionRedirectPath() || "/login"}
      className="flex items-center gap-1.5 px-2"
    >
      <img src="/static/logos/farm2go.svg" alt={t("header.farm2go")} className="size-6" />
      <p className="font-bold text-base group-data-[collapsible=icon]:hidden">
        {t("header.farm2go")}
      </p>
    </Link>
  );
};

const ManagerSidebar = ({
  activeSectionId,
  country,
  navigation,
  onCountryChange,
  user,
}: ManagerSidebarProps) => {
  const { pathname } = useLocation();
  const { isMobile, setOpenMobile, state } = useSidebar();
  const [openSectionIds, setOpenSectionIds] = useState<string[]>([]);
  const [revealedSectionId, setRevealedSectionId] = useState<string>();

  if (activeSectionId !== revealedSectionId) {
    setRevealedSectionId(activeSectionId);

    if (activeSectionId && !openSectionIds.includes(activeSectionId)) {
      setOpenSectionIds([...openSectionIds, activeSectionId]);
    }
  }

  useEffect(() => {
    setOpenMobile(false);
  }, [pathname, setOpenMobile]);

  const handleSectionOpenChange = (sectionId: string, open: boolean) =>
    setOpenSectionIds(ids => (open ? [...ids, sectionId] : ids.filter(id => id !== sectionId)));

  const isCollapsed = state === "collapsed" && !isMobile;

  return (
    <Sidebar collapsible="icon" className="z-40">
      <SidebarHeader className="gap-4 px-2 py-5">
        <Logo />
        <CountrySwitcher country={country} onCountryChange={onCountryChange} />
      </SidebarHeader>
      <SidebarContent className="group-data-[collapsible=icon]:overflow-visible">
        {navigation.overview && (
          <SidebarGroup className="pb-0">
            <SidebarMenu className="group-data-[collapsible=icon]:items-start">
              <SidebarNavItem item={navigation.overview} />
            </SidebarMenu>
          </SidebarGroup>
        )}
        {isCollapsed ? (
          <CollapsedNav activeSectionId={activeSectionId} sections={navigation.sections} />
        ) : (
          <ExpandedNav
            openSectionIds={openSectionIds}
            onSectionOpenChange={handleSectionOpenChange}
            sections={navigation.sections}
          />
        )}
      </SidebarContent>
      <SidebarFooter className="gap-3 pb-4">
        <SidebarSeparator className="mx-2" />
        <SidebarUserMenu user={user} />
      </SidebarFooter>
      {!isMobile && <SidebarToggle />}
    </Sidebar>
  );
};

export const WfpManagerMainNavigation = ({
  children,
  country,
  onCountryChange,
  user,
}: WfpManagerMainNavigationProps) => {
  const { pathname } = useLocation();
  const navigation = useMemo(() => getVisibleNavigation(user), [user]);
  const activeTrail = getActiveTrail(pathname, navigation);

  return (
    <SidebarProvider style={sidebarStyle}>
      <ManagerSidebar
        activeSectionId={activeTrail.section?.id}
        country={country}
        navigation={navigation}
        onCountryChange={onCountryChange}
        user={user}
      />
      <SidebarInset className="min-w-0">
        <header className="flex h-14 shrink-0 items-center gap-3 border-b bg-background px-4 md:hidden">
          <SidebarTrigger />
          <Logo />
        </header>
        {children}
      </SidebarInset>
    </SidebarProvider>
  );
};
